import { schemas } from '@recurrency/core-api-schema';
import {
  OrderCycleSource,
  RecurrencyReplenishmentMethod,
  SupplierTargetTypes,
} from '@recurrency/core-api-schema/dist/common/enums';
import { InheritancePairDTO } from '@recurrency/core-api-schema/dist/itemLocations/validateInheritancePairs';
import { BulkItemSettingsUpdateDTO, ItemSettingsUpdateDTO } from '@recurrency/core-api-schema/dist/items/itemSettings';
import {
  ItemLocationUpdate,
  UpdateItemLocationInfoType,
} from '@recurrency/core-api-schema/dist/purchasing/postUpdateItemLocationInfo';
import { MinMaxUpdate } from '@recurrency/core-api-schema/dist/purchasing/postUpdateItemLocationMinMax';
import { RcFile } from 'antd/lib/upload';
import { AxiosResponse } from 'axios';
import moment from 'moment';

import { BulkImportModal } from 'components/recipes/BulkImportModal/BulkImportModal';

import { coreApiFetch } from 'utils/api';
import { csvRowToArray } from 'utils/array';
import { showAsyncModal } from 'utils/asyncModal';
import { captureError } from 'utils/error';

export const BulkImportName = {
  ExcludeInclude: 'Exclude/Include',
  SafetyStock: 'Safety Stock',
  LeadTime: 'Lead Time',
  StockFloor: 'Stock Floor',
  MinimumOrderQuantity: 'Minimum Order Quantity',
  MinMax: 'Min/Max',
  PlannableInventory: 'Plannable Inventory',
  ItemInheritance: 'Item Inheritance',
  SupplierTargets: 'Supplier Targets',
  SupplierMinimums: 'Supplier Minimums',
  SupplierReviewCycle: 'Supplier Review Cycle',
  SupplierOrderCycle: 'Supplier Order Cycle',
  MaxShelfLife: 'Max Shelf Life',
};

export type BulkImportRequestBase =
  | MinMaxUpdate
  | ItemLocationUpdate
  | InheritancePairDTO
  | SupplierLocationUpdate
  | ItemSettingsUpdateDTO;

export interface BulkImportInput {
  name: string;
  isPrimaryKey: boolean;
  /** Defaults to non optional */
  optional?: boolean;
  validation: (val: string, field?: string) => ValidationResult;
  /** To format the value after it has been read from the csv (used for converting strings to numbers, etc) */
  format?: (val: string) => unknown;
}

export interface SupplierLocationUpdate {
  supplierId: string;
  locationId: string;
  supplierName?: string;
  locationName?: string;
  targetType?: SupplierTargetTypes;
  targetValue?: number;
  minTargetType?: SupplierTargetTypes;
  minTargetValue?: number;
  reviewCycleDays?: number;
  orderCycleSource?: OrderCycleSource;
  orderCycleDaysOverride?: number;
}

export interface BulkImportModalProps<BulkImportRequest extends BulkImportRequestBase> {
  onClose: () => void;
  inputs: BulkImportInput[];
  /** Name of import */
  name: string;
  onUpdate: (updates: BulkImportRequest[]) => Promise<AxiosResponse>;
  onValidate?: (
    rowList: BulkImportRequest[],
    dataByKey: Record<string, BulkImportRequest>,
  ) => Promise<{
    valid: BulkImportRequest[];
    invalid: BulkImportRequest[];
  }>;
}
/**
 * Validator funtions
 */
export type NonUpdateFields = 'itemUid' | 'locationId' | 'itemId';
export type ValidationResult = string | null;

// Positive integers only or decimal with trailing 0
export const isNonNegativeIntegerString = (value: string, field?: string): ValidationResult =>
  // digits 0-9 or float with only trailing 0's. Captures positive ints
  value.match(/^\d+(?:\.0+)?$/) === null ? generateValidationErrorMessage(field || 'number', value) : null;

// Non-negative integers (0 allowed) or decimal with trailing 0's (ex: 1.00 is valid) or empty string
export const isNullableNonNegativeIntegerString = (value: string): ValidationResult =>
  value.trim() === '' ? null : isNonNegativeIntegerString(value.trim(), 'number');

export const generateValidationErrorMessage = (type: string, value: string, suffix = ''): string =>
  `Some values aren't valid ${type} (e.g. "${value}"). Please check your CSV and try again.${
    suffix ? ` ${suffix}` : ''
  }`;

export const validateReplenishmentMethod = (input: string): string | null => {
  const validReplenishmentMethods = [
    RecurrencyReplenishmentMethod.MinMax.toLowerCase(),
    RecurrencyReplenishmentMethod.OPOQ.toLowerCase(),
  ];
  return validReplenishmentMethods.includes(input.toLowerCase()) || input.length === 0
    ? null
    : generateValidationErrorMessage(
        'replenishment method',
        input,
        `Valid methods are: ${validReplenishmentMethods.join(', ')}`,
      );
};

export const formatReplenishmentMethod = (input: string): string | null => {
  if (input.toLowerCase() === RecurrencyReplenishmentMethod.MinMax.toLowerCase()) {
    return RecurrencyReplenishmentMethod.MinMax;
  }
  if (input.toLowerCase() === RecurrencyReplenishmentMethod.OPOQ.toLowerCase()) {
    return RecurrencyReplenishmentMethod.OPOQ;
  }
  return null;
};
export const validateIsBoolean = (str: string): ValidationResult =>
  str.toLowerCase() === 'true' || str.toLowerCase() === 'false' ? null : generateValidationErrorMessage('boolean', str);

export const isNullableBoolean = (value: string): ValidationResult =>
  value.trim() === '' ? null : validateIsBoolean(value);

export const toBoolean = (str: string): boolean | null => {
  if (str.toLowerCase() === 'true') return true;
  if (str.toLowerCase() === 'false') return false;
  return null; // Invalid input that should be validated against
};

export const validateIsISODate = (str: string): ValidationResult => {
  if (str === '' || moment(str, moment.ISO_8601, true).isValid()) {
    return null;
  }
  return generateValidationErrorMessage('ISO Date', str);
};

export const itemLocationInputs: BulkImportInput[] = [
  {
    name: 'itemUid',
    isPrimaryKey: true,
    validation: (val) => isNonNegativeIntegerString(val, 'itemUid'),
  },
  {
    name: 'locationId',
    isPrimaryKey: true,
    validation: (val) => isNonNegativeIntegerString(val, 'locationId'),
  },
];

export const excludeIncludeInputs: BulkImportInput[] = [
  ...itemLocationInputs,
  {
    name: 'excluded',
    isPrimaryKey: false,
    validation: validateIsBoolean,
    format: toBoolean,
  },
  {
    name: 'excludedUntil',
    isPrimaryKey: false,
    optional: true,
    validation: validateIsISODate,
    format: (value: string) => value || null,
  },
];

export const safetyStockInputs: BulkImportInput[] = [
  ...itemLocationInputs,
  {
    name: 'safetyStockDays',
    isPrimaryKey: false,
    validation: isNullableNonNegativeIntegerString,
    format: (val: string) => (val ? parseInt(val, 10) : null),
  },
];

export const leadTimeInputs: BulkImportInput[] = [
  ...itemLocationInputs,
  {
    name: 'leadTimeDays',
    isPrimaryKey: false,
    validation: isNullableNonNegativeIntegerString,
    format: (val: string) => (val ? parseInt(val, 10) : null),
  },
];

export const stockFloorInputs: BulkImportInput[] = [
  ...itemLocationInputs,
  {
    name: 'minStockFloor',
    isPrimaryKey: false,
    validation: isNullableNonNegativeIntegerString,
    format: (val: string) => (val && parseInt(val, 10) > 0 ? parseInt(val, 10) : null),
  },
];

export const minimumOrderQuantityInputs: BulkImportInput[] = [
  ...itemLocationInputs,
  {
    name: 'minOrderQuantity',
    isPrimaryKey: false,
    validation: isNullableNonNegativeIntegerString,
    format: (val: string) => (val && parseInt(val, 10) > 0 ? parseInt(val, 10) : null),
  },
];

export const minMaxInputs: BulkImportInput[] = [
  ...itemLocationInputs,
  {
    name: 'newMin',
    isPrimaryKey: false,
    validation: isNonNegativeIntegerString,
    format: (val: string) => parseInt(val, 10),
  },
  {
    name: 'newMax',
    isPrimaryKey: false,
    validation: isNonNegativeIntegerString,
    format: (val: string) => parseInt(val, 10),
  },
  {
    name: 'newReplenishmentMethod',
    isPrimaryKey: false,
    validation: validateReplenishmentMethod,
    format: formatReplenishmentMethod,
  },
];

export const plannableInputs: BulkImportInput[] = [
  ...itemLocationInputs,
  {
    name: 'plannable',
    isPrimaryKey: false,
    validation: isNullableBoolean,
    format: toBoolean,
  },
];

export const validateSupplierTargetType = (input: string): string | null => {
  const validTypes = [
    SupplierTargetTypes.Dollars,
    SupplierTargetTypes.Units,
    SupplierTargetTypes.Weight,
    SupplierTargetTypes.Volume,
  ];
  return input.length === 0 || validTypes.includes(input.toLowerCase() as SupplierTargetTypes)
    ? null
    : generateValidationErrorMessage('target type', input, `Valid types are: ${validTypes.join(', ')}`);
};

export const supplierLocationInputs: BulkImportInput[] = [
  {
    name: 'supplierId',
    isPrimaryKey: true,
    validation: (val) => isNonNegativeIntegerString(val, 'supplierId'),
  },
  {
    name: 'locationId',
    isPrimaryKey: true,
    validation: (val) => isNonNegativeIntegerString(val, 'locationId'),
  },
];

export const supplierTargetInputs: BulkImportInput[] = [
  ...supplierLocationInputs,
  {
    name: 'targetType',
    isPrimaryKey: false,
    validation: validateSupplierTargetType,
    format: (val: string) => (val ? val.toLowerCase() : null),
  },
  {
    name: 'targetValue',
    isPrimaryKey: false,
    validation: isNullableNonNegativeIntegerString,
    format: (val: string) => (val.trim() ? parseFloat(val) : null),
  },
];

export const supplierMinimumInputs: BulkImportInput[] = [
  ...supplierLocationInputs,
  {
    name: 'minTargetType',
    isPrimaryKey: false,
    validation: validateSupplierTargetType,
    format: (val: string) => (val ? val.toLowerCase() : null),
  },
  {
    name: 'minTargetValue',
    isPrimaryKey: false,
    validation: isNullableNonNegativeIntegerString,
    format: (val: string) => (val.trim() ? parseFloat(val) : null),
  },
];

export const supplierReviewCycleInputs: BulkImportInput[] = [
  ...supplierLocationInputs,
  {
    name: 'reviewCycleDays',
    isPrimaryKey: false,
    validation: isNonNegativeIntegerString,
    format: (val: string) => parseInt(val, 10),
  },
];

export const supplierOrderCycleInputs: BulkImportInput[] = [
  ...supplierLocationInputs,
  {
    name: 'orderCycleDaysOverride',
    isPrimaryKey: false,
    validation: isNonNegativeIntegerString,
    format: (val: string) => parseInt(val, 10),
  },
];

export const itemInheritanceInputs: BulkImportInput[] = [
  {
    name: 'ancestorItemUid',
    isPrimaryKey: true,
    validation: (val) => isNonNegativeIntegerString(val, 'ancestorItemUid'),
  },
  {
    name: 'descendantItemUid',
    isPrimaryKey: true,
    validation: (val) => isNonNegativeIntegerString(val, 'descendantItemUid'),
  },
];

/** itemUid or itemCode required (at least one); empty or 0 maxShelfLifeDays clears the setting. */
export const maxShelfLifeInputs: BulkImportInput[] = [
  {
    name: 'itemUid',
    isPrimaryKey: true,
    optional: true,
    validation: (val) => (val.trim() === '' ? null : isNonNegativeIntegerString(val.trim(), 'itemUid')),
    format: (val: string) => val.trim() || null,
  },
  {
    name: 'itemCode',
    isPrimaryKey: true,
    optional: true,
    validation: () => null,
    format: (val: string) => val.trim() || null,
  },
  {
    name: 'maxShelfLifeDays',
    isPrimaryKey: false,
    optional: true,
    validation: isNullableNonNegativeIntegerString,
    format: (val: string) => (val && parseInt(val, 10) > 0 ? parseInt(val, 10) : null),
  },
];

export async function validateItemLocationRows<T extends MinMaxUpdate | ItemLocationUpdate>(
  rowList: T[],
  dataByKey: Record<string, T>,
): Promise<{ valid: T[]; invalid: T[] }> {
  const { data: validationResponse } = await coreApiFetch(schemas.itemLocations.validateCsvUpload, {
    bodyParams: {
      itemLocations: rowList.map((item) => ({
        itemId: item.itemUid,
        locationId: item.locationId,
      })),
    },
  });

  if (!validationResponse) {
    return {
      valid: [],
      invalid: [],
    };
  }

  const mapItems = (items: typeof validationResponse.valid) =>
    items.map((item) => ({
      ...dataByKey[`${item.itemId}|${item.locationId}`],
      itemUid: item.itemId,
      locationId: item.locationId,
      itemId: item.itemCode,
    })) as (T & { itemId: string })[];
  return {
    valid: mapItems(validationResponse.valid),
    invalid: mapItems(validationResponse.invalid),
  };
}

export async function validateInheritancePairRows<T extends InheritancePairDTO>(
  rowList: T[],
  _dataByKey: Record<string, T>,
): Promise<{ valid: T[]; invalid: T[] }> {
  const { data: validationResponse } = await coreApiFetch(schemas.itemLocations.validateInheritancePairs, {
    bodyParams: {
      inheritancePairs: rowList.map((item) => ({
        ancestorItemUid: item.ancestorItemUid,
        descendantItemUid: item.descendantItemUid,
      })),
    },
  });

  if (!validationResponse) {
    return {
      valid: [],
      invalid: [],
    };
  }

  return {
    valid: validationResponse.valid as T[],
    invalid: validationResponse.invalid as T[],
  };
}

export async function validateItemSettingsRows(
  rowList: ItemSettingsUpdateDTO[],
  dataByKey: Record<string, ItemSettingsUpdateDTO>,
): Promise<{ valid: ItemSettingsUpdateDTO[]; invalid: ItemSettingsUpdateDTO[] }> {
  // Identity keys only — shelf-life values stay on the client upload rows.
  const { data: validationResponse } = await coreApiFetch(schemas.items.validateItemSettingsCsvUpload, {
    bodyParams: {
      items: rowList.map((item) => ({
        ...(item.itemUid ? { itemUid: item.itemUid } : {}),
        ...(item.itemCode ? { itemCode: item.itemCode } : {}),
      })),
    },
  });

  if (!validationResponse) {
    return { valid: [], invalid: [] };
  }

  // Prefer itemUid match; fall back to itemCode-only upload rows.
  // Parse already dedupes per identity key, so a single Map entry per key is enough.
  const mapItems = (items: ItemSettingsUpdateDTO[]) => {
    const byItemUid = new Map<string, ItemSettingsUpdateDTO>();
    const byItemCode = new Map<string, ItemSettingsUpdateDTO>();
    for (const row of Object.values(dataByKey)) {
      if (row.itemUid) {
        byItemUid.set(row.itemUid, row);
      } else if (row.itemCode) {
        byItemCode.set(row.itemCode, row);
      }
    }

    const takeOriginal = (resolved: ItemSettingsUpdateDTO) => {
      if (resolved.itemUid && byItemUid.has(resolved.itemUid)) {
        const row = byItemUid.get(resolved.itemUid)!;
        byItemUid.delete(resolved.itemUid);
        return row;
      }
      if (resolved.itemCode && byItemCode.has(resolved.itemCode)) {
        const row = byItemCode.get(resolved.itemCode)!;
        byItemCode.delete(resolved.itemCode);
        return row;
      }
      return undefined;
    };

    return items.map((item) => {
      const original = takeOriginal(item);
      const itemCode = item.itemCode ?? original?.itemCode ?? undefined;
      return {
        ...(original ?? {}),
        itemUid: item.itemUid,
        itemCode,
        // Review table link column expects itemId (item code)
        itemId: itemCode,
      } as ItemSettingsUpdateDTO & { itemId?: string };
    });
  };

  const valid = mapItems(validationResponse.valid);
  const invalid = mapItems(validationResponse.invalid);

  // Same resolved item via itemUid + itemCode → treat all colliding rows as invalid.
  const resolvedUidCounts = new Map<string, number>();
  for (const item of valid) {
    if (item.itemUid) {
      resolvedUidCounts.set(item.itemUid, (resolvedUidCounts.get(item.itemUid) ?? 0) + 1);
    }
  }

  const uniqueValid: ItemSettingsUpdateDTO[] = [];
  const collisionInvalid: ItemSettingsUpdateDTO[] = [];
  for (const item of valid) {
    if (item.itemUid && (resolvedUidCounts.get(item.itemUid) ?? 0) > 1) {
      collisionInvalid.push(item);
    } else {
      uniqueValid.push(item);
    }
  }

  return {
    valid: uniqueValid,
    invalid: [...invalid, ...collisionInvalid],
  };
}

export function showExcludeIncludeCSVModal() {
  return showAsyncModal(
    (props) =>
      BulkImportModal<ItemLocationUpdate>({
        name: BulkImportName.ExcludeInclude,
        inputs: excludeIncludeInputs,
        onUpdate: async (updates: ItemLocationUpdate[]) =>
          coreApiFetch(schemas.purchasing.postUpdateItemLocationInfo, {
            pathParams: {
              updateType: UpdateItemLocationInfoType.Exclude,
            },
            bodyParams: {
              updates,
            },
          }),

        ...props,
      }),
    {},
  );
}

export function showSafetyStockCSVModal() {
  return showAsyncModal(
    (props) =>
      BulkImportModal<ItemLocationUpdate>({
        name: BulkImportName.SafetyStock,
        inputs: safetyStockInputs,
        onUpdate: async (updates: ItemLocationUpdate[]) =>
          coreApiFetch(schemas.purchasing.postUpdateItemLocationInfo, {
            pathParams: {
              updateType: UpdateItemLocationInfoType.SafetyStock,
            },
            bodyParams: {
              updates,
            },
          }),
        ...props,
      }),
    {},
  );
}

export function showLeadTimeCSVModal() {
  return showAsyncModal(
    (props) =>
      BulkImportModal<ItemLocationUpdate>({
        name: BulkImportName.LeadTime,
        inputs: leadTimeInputs,
        onUpdate: async (updates: ItemLocationUpdate[]) =>
          coreApiFetch(schemas.purchasing.postUpdateItemLocationInfo, {
            pathParams: {
              updateType: UpdateItemLocationInfoType.LeadTime,
            },
            bodyParams: {
              updates,
            },
          }),
        ...props,
      }),
    {},
  );
}

export function showStockFloorModal() {
  return showAsyncModal(
    (props) =>
      BulkImportModal<ItemLocationUpdate>({
        name: BulkImportName.StockFloor,
        inputs: stockFloorInputs,
        onUpdate: async (updates: ItemLocationUpdate[]) =>
          coreApiFetch(schemas.purchasing.postUpdateItemLocationInfo, {
            pathParams: {
              updateType: UpdateItemLocationInfoType.MinStockFloor,
            },
            bodyParams: {
              updates,
            },
          }),
        ...props,
      }),
    {},
  );
}

export function showMinimumOrderQuantityModal() {
  return showAsyncModal(
    (props) =>
      BulkImportModal<ItemLocationUpdate>({
        name: BulkImportName.MinimumOrderQuantity,
        inputs: minimumOrderQuantityInputs,
        onUpdate: async (updates: ItemLocationUpdate[]) =>
          coreApiFetch(schemas.purchasing.postUpdateItemLocationInfo, {
            pathParams: {
              updateType: UpdateItemLocationInfoType.MinOrderQuantity,
            },
            bodyParams: {
              updates,
            },
          }),
        ...props,
      }),
    {},
  );
}
export function showMinMaxCSVModal() {
  return showAsyncModal(
    (props) =>
      BulkImportModal<MinMaxUpdate>({
        name: BulkImportName.MinMax,
        inputs: minMaxInputs,
        onUpdate: async (updates: MinMaxUpdate[]) =>
          coreApiFetch(schemas.purchasing.postUpdateItemLocationMinMax, {
            bodyParams: {
              updates,
            },
          }),
        ...props,
      }),
    {},
  );
}
export function showPlannableCSVModal() {
  return showAsyncModal(
    (props) =>
      BulkImportModal<ItemLocationUpdate>({
        name: BulkImportName.PlannableInventory,

        inputs: plannableInputs,
        onUpdate: async (updates: ItemLocationUpdate[]) =>
          coreApiFetch(schemas.purchasing.postUpdateItemLocationInfo, {
            pathParams: {
              updateType: UpdateItemLocationInfoType.Plannable,
            },
            bodyParams: {
              updates,
            },
          }),

        ...props,
      }),
    {},
  );
}
export function showItemInheritanceCSVModal() {
  return showAsyncModal(
    (props) =>
      BulkImportModal<InheritancePairDTO>({
        name: BulkImportName.ItemInheritance,
        inputs: itemInheritanceInputs,
        onValidate: validateInheritancePairRows,
        onUpdate: async (updates: InheritancePairDTO[]) =>
          coreApiFetch(schemas.items.bulkUpsertItemUsageInheritanceOverrides, {
            bodyParams: { inheritancePairs: updates },
          }),
        ...props,
      }),
    {},
  );
}

export async function validateSupplierLocationRows<T extends SupplierLocationUpdate>(
  rowList: T[],
  dataByKey: Record<string, T>,
): Promise<{ valid: T[]; invalid: T[] }> {
  const { data: validationResponse } = await coreApiFetch(schemas.supplierLocations.validateSupplierLocations, {
    bodyParams: {
      supplierLocations: rowList.map((item) => ({
        supplierId: item.supplierId,
        locationId: item.locationId,
      })),
    },
  });

  if (!validationResponse) {
    return { valid: [], invalid: [] };
  }

  return {
    valid: validationResponse.valid.map((item) => ({
      ...dataByKey[`${item.supplierId}|${item.locationId}`],
      supplierName: item.supplierName,
      locationName: item.locationName,
    })) as T[],
    invalid: validationResponse.invalid.map((item) => ({
      ...dataByKey[`${item.supplierId}|${item.locationId}`],
    })),
  };
}

export function showSupplierTargetsCSVModal() {
  return showAsyncModal(
    (props) =>
      BulkImportModal<SupplierLocationUpdate>({
        name: BulkImportName.SupplierTargets,
        inputs: supplierTargetInputs,
        onValidate: validateSupplierLocationRows,
        onUpdate: async (updates: SupplierLocationUpdate[]) =>
          coreApiFetch(schemas.purchasing.patchBulkSupplierLocationSettings, {
            bodyParams: {
              settings: updates.map((u) => ({
                supplierId: u.supplierId,
                locationId: u.locationId,
                targetType: u.targetValue && Number(u.targetValue) > 0 ? u.targetType : SupplierTargetTypes.NotSet,
                targetValue: u.targetValue ?? undefined,
              })),
            },
          }),
        ...props,
      }),
    {},
  );
}

export function showSupplierMinimumsCSVModal() {
  return showAsyncModal(
    (props) =>
      BulkImportModal<SupplierLocationUpdate>({
        name: BulkImportName.SupplierMinimums,
        inputs: supplierMinimumInputs,
        onValidate: validateSupplierLocationRows,
        onUpdate: async (updates: SupplierLocationUpdate[]) =>
          coreApiFetch(schemas.purchasing.patchBulkSupplierLocationSettings, {
            bodyParams: {
              settings: updates.map((u) => ({
                supplierId: u.supplierId,
                locationId: u.locationId,
                minTargetType:
                  u.minTargetValue && Number(u.minTargetValue) > 0 ? u.minTargetType : SupplierTargetTypes.NotSet,
                minTargetValue: u.minTargetValue ?? undefined,
              })),
            },
          }),
        ...props,
      }),
    {},
  );
}

export function showSupplierReviewCycleCSVModal() {
  return showAsyncModal(
    (props) =>
      BulkImportModal<SupplierLocationUpdate>({
        name: BulkImportName.SupplierReviewCycle,
        inputs: supplierReviewCycleInputs,
        onValidate: validateSupplierLocationRows,
        onUpdate: async (updates: SupplierLocationUpdate[]) =>
          coreApiFetch(schemas.purchasing.patchBulkSupplierLocationSettings, {
            bodyParams: {
              settings: updates.map((u) => ({
                supplierId: u.supplierId,
                locationId: u.locationId,
                reviewCycleDays: u.reviewCycleDays,
              })),
            },
          }),
        ...props,
      }),
    {},
  );
}

export function showSupplierOrderCycleCSVModal() {
  return showAsyncModal(
    (props) =>
      BulkImportModal<SupplierLocationUpdate>({
        name: BulkImportName.SupplierOrderCycle,
        inputs: supplierOrderCycleInputs,
        onValidate: validateSupplierLocationRows,
        onUpdate: async (updates: SupplierLocationUpdate[]) =>
          coreApiFetch(schemas.purchasing.patchBulkSupplierLocationSettings, {
            bodyParams: {
              settings: updates.map((u) => ({
                supplierId: u.supplierId,
                locationId: u.locationId,
                orderCycleSource: OrderCycleSource.Override, // always set to override
                orderCycleDaysOverride: u.orderCycleDaysOverride,
              })),
            },
          }),
        ...props,
      }),
    {},
  );
}

export function showMaxShelfLifeCSVModal() {
  return showAsyncModal(
    (props) =>
      BulkImportModal<ItemSettingsUpdateDTO>({
        name: BulkImportName.MaxShelfLife,
        inputs: maxShelfLifeInputs,
        onValidate: validateItemSettingsRows,
        onUpdate: async (updates: ItemSettingsUpdateDTO[]) =>
          coreApiFetch(schemas.items.bulkUpsertItemSettings, {
            bodyParams: {
              updates: updates.flatMap((u): BulkItemSettingsUpdateDTO[] =>
                u.itemUid ? [{ itemUid: u.itemUid, maxShelfLifeDays: u.maxShelfLifeDays ?? null }] : [],
              ),
            },
          }),
        ...props,
      }),
    {},
  );
}

export const downloadHeaderTemplate = async (headers: string[], name: string) => {
  const blob = new Blob([headers.join(',')], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `bulk_upload_${name}_template.csv`;
  a.click();
};

export const parseUploadedData = async <BulkImportRequest extends BulkImportRequestBase>(
  file: RcFile,
  inputs: BulkImportInput[],
  handleValidated: (rowList: BulkImportRequest[], dataByKey: Record<string, BulkImportRequest>) => Promise<void>,
  setIsUploading: (isUploading: boolean) => void,
  setError: (error: string) => void,
  setFileName: (fileName: string) => void,
): Promise<void> => {
  try {
    setIsUploading(true);
    const reader = new FileReader();
    const inputMap: Record<keyof BulkImportRequest, BulkImportInput> = inputs.reduce((acc, val) => {
      acc[val.name as keyof BulkImportRequest] = val;
      return acc;
    }, {} as Record<keyof BulkImportRequest, BulkImportInput>);
    const allowedColumns = Object.keys(inputMap);
    const primaryKeys = inputs.filter((val) => val.isPrimaryKey).map((val) => val.name);

    // Filereader runs on a separate thread. To ensure api validation is complete, wrap in a promise
    await new Promise<void>((resolve, reject) => {
      reader.onload = async (e) => {
        const text = e.target?.result as string;
        const lines = text.split('\n');
        const userUploadedColumns = csvRowToArray(lines[0].replace('\r', '').trim());
        const validationErrors = validateColumns(userUploadedColumns, allowedColumns);
        if (validationErrors) {
          setError(validationErrors);
          return resolve();
        }

        // Initial line validation
        const filteredLines = lines
          .slice(1)
          .map((line) => line.trim())
          .filter((s) => s !== '');

        const uploadedData: BulkImportRequest[] = [];
        const deduplicationSet: Record<string, BulkImportRequest> = {};

        for (const [rowIndex, line] of filteredLines.entries()) {
          const valuesAsStringArray = csvRowToArray(line.trim());

          // Validate number of columns in row
          if (valuesAsStringArray.length !== userUploadedColumns.length) {
            setError(`Invalid csv format, line ${rowIndex + 1} does not have the same number of columns as the header`);
            return resolve();
          }

          // Create object from CSV row by iterating through each column value in the row
          const rowObject: Partial<BulkImportRequest> = {};
          for (const [index, columnName] of (userUploadedColumns as [keyof BulkImportRequest]).entries()) {
            // current column info from inputs array
            const columnInput = inputMap[columnName];
            // value of the current column for the header
            const columnValue = valuesAsStringArray[index].trim();

            const validationResult = columnInput.validation(columnValue);
            if (validationResult !== null) {
              setError(`Line ${rowIndex + 1}: ${validationResult}`);
              return resolve();
            }
            rowObject[columnName] = (
              columnInput.format ? columnInput.format(columnValue) : columnValue
            ) as BulkImportRequest[keyof BulkImportRequest];
          }
          const allPrimaryKeysOptional =
            primaryKeys.length > 0 &&
            primaryKeys.every((column) => inputMap[column as keyof BulkImportRequest]?.optional);

          // When every primary key is optional (e.g. itemUid OR itemCode), require at least one
          if (
            allPrimaryKeysOptional &&
            primaryKeys.every((column) => {
              const value = rowObject[column as keyof BulkImportRequest];
              return value == null || value === '';
            })
          ) {
            setError(`Line ${rowIndex + 1}: at least one of ${primaryKeys.join(' or ')} is required`);
            return resolve();
          }

          // Optional PKs: key by the first present identity column+value (prefer itemUid over
          // itemCode) so the same item cannot appear twice under different secondary keys, while
          // still allowing itemUid=123 and itemCode=123 as distinct identities.
          // Required PKs: keep composite key (e.g. itemUid|locationId).
          const key = allPrimaryKeysOptional
            ? (() => {
                const column = primaryKeys.find((primaryKey) => {
                  const value = rowObject[primaryKey as keyof BulkImportRequest];
                  return value != null && value !== '';
                });
                return column ? `${column}:${String(rowObject[column as keyof BulkImportRequest])}` : '';
              })()
            : primaryKeys.map((column) => rowObject[column as keyof BulkImportRequest]).join('|');

          // ensure it is not a duplicate
          if (deduplicationSet[key] !== undefined) {
            setError(`Duplicate row number "${rowIndex + 1}" included in csv`);
            return resolve();
          }

          deduplicationSet[key] = rowObject as BulkImportRequest;
          uploadedData.push(rowObject as BulkImportRequest);
        }
        await handleValidated(uploadedData, deduplicationSet);
        return resolve();
      };
      reader.onerror = (ev) => {
        reject(ev.target?.error);
      };
      reader.readAsText(file);
      setFileName(file.name);
    });
  } catch (error) {
    captureError(error);
    setError('Error uploading data');
  } finally {
    setIsUploading(false);
  }
};
export const validateColumns = (columns: string[], allowedColumns: string[]) => {
  const columnSet = new Set<string>();
  const unknownColumns = [];
  for (const column of columns) {
    if (columnSet.has(column)) {
      return `Duplicate column "${column}" included in csv`;
    }
    columnSet.add(column);
    // expected columns are just the ones in the set
    if (!allowedColumns.includes(column)) {
      unknownColumns.push(column);
    }
  }
  if (unknownColumns.length > 0) {
    return `Unknown column(s) found: ${unknownColumns.join(
      ', ',
    )} \n Make sure your CSV matches the template before uploading.`;
  }
  const missingColumns = allowedColumns.filter((allowedColumn) => !columnSet.has(allowedColumn));
  if (missingColumns.length > 0) return `Missing column(s) "${missingColumns.join(', ')}" in csv`;
  return null;
};
